// Extracted verbatim from pages/coach/Schedule.jsx (2026-06-08 P3 refactor).
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, ChevronRight, Loader2, X } from "lucide-react";
import {
    DAY_LABELS, DAY_LABELS_LONG, HALF_HOUR_TIMES, formatTimeDisplay,
    isoDate, startOfWeekMon, addDays, weekRangeLabel, jsDayToMonIdx,
} from "@/lib/schedule";
import { CATEGORIES, CATEGORY_LABELS } from "@/lib/workoutTaxonomy";
import { ModalShell, Field, inputClass } from "./ModalShell";
import AthleteNameLink from "@/components/coach/AthleteNameLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RecentDeductionsPanel({ bump }) {
    const [date, setDate] = useState(() => isoDate(new Date()));
    const [data, setData] = useState({ events: [], summary: { total: 0, deductions: 0, ious: 0 } });
    const [loading, setLoading] = useState(true);
    const [collapsed, setCollapsed] = useState(false);
    const [resolvingIou, setResolvingIou] = useState(null); // ev being resolved

    const load = async (d) => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/coach/recent-deductions`, {
                params: { date: d, limit: 100 },
                withCredentials: true,
            });
            setData(r.data || { events: [], summary: { total: 0, deductions: 0, ious: 0 } });
        } catch (e) {
            toast.error("Couldn't load deductions", { description: e?.response?.data?.detail });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(date);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, bump]);

    const isToday = date === isoDate(new Date());

    return (
        <div data-testid="recent-deductions-panel" className="border border-zinc-800 rounded-2xl bg-zinc-950/50 overflow-hidden">
            <button
                type="button"
                onClick={() => setCollapsed((c) => !c)}
                className="w-full px-5 py-3 border-b border-zinc-800 flex items-center justify-between hover:bg-zinc-900/40 transition-colors text-left"
                data-testid="recent-deductions-toggle"
            >
                <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-[11px] tracking-[0.25em] uppercase text-zinc-200 font-semibold">
                        Session deductions
                    </h3>
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 border border-emerald-700/60 bg-emerald-950/40 text-emerald-300">
                        <CheckCircle2 className="w-3 h-3" /> {data.summary.deductions} burned
                    </span>
                    {data.summary.ious > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 border border-rose-600/70 bg-rose-950/50 text-rose-300 font-bold">
                            <AlertCircle className="w-3 h-3" /> {data.summary.ious} IOU(s) — no session deducted
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-300">
                        {isToday ? "Today" : date}
                    </span>
                    <ChevronRight className={`w-4 h-4 transition-transform ${collapsed ? "" : "rotate-90"}`} />
                </div>
            </button>

            {!collapsed && (
                <>
                    <div className="px-5 py-2 border-b border-zinc-900 flex items-center gap-2 flex-wrap text-xs bg-zinc-950/70">
                        <span className="text-zinc-300">Audit date:</span>
                        <input
                            type="date"
                            value={date}
                            data-testid="recent-deductions-date"
                            onChange={(e) => setDate(e.target.value)}
                            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-xs text-white focus:border-brand-red focus:outline-none"
                        />
                        <button
                            type="button"
                            data-testid="recent-deductions-today"
                            onClick={() => setDate(isoDate(new Date()))}
                            className="px-2 py-1 text-[10px] tracking-[0.2em] uppercase border border-zinc-700 hover:border-brand-red hover:text-brand-red"
                        >
                            Today
                        </button>
                        <button
                            type="button"
                            data-testid="recent-deductions-yesterday"
                            onClick={() => setDate(isoDate(addDays(new Date(), -1)))}
                            className="px-2 py-1 text-[10px] tracking-[0.2em] uppercase border border-zinc-700 hover:border-zinc-500"
                        >
                            Yesterday
                        </button>
                        <button
                            type="button"
                            onClick={() => load(date)}
                            disabled={loading}
                            data-testid="recent-deductions-refresh"
                            className="ml-auto px-2 py-1 text-[10px] tracking-[0.2em] uppercase border border-zinc-700 hover:border-brand-red hover:text-brand-red disabled:opacity-50"
                        >
                            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Refresh"}
                        </button>
                    </div>

                    <div className="divide-y divide-zinc-900 max-h-[28rem] overflow-y-auto">
                        {loading && data.events.length === 0 ? (
                            <div className="px-5 py-6 text-center text-xs text-zinc-500">
                                <Loader2 className="w-4 h-4 animate-spin inline-block" /> Loading…
                            </div>
                        ) : data.events.length === 0 ? (
                            <div className="px-5 py-6 text-center text-xs text-zinc-500">
                                No check-ins {isToday ? "yet today" : "on this day"}. Walk-ins and Mark Present clicks will appear here.
                            </div>
                        ) : data.events.map((ev, i) => (
                            <DeductionRow
                                key={`${ev.source}-${ev.booking_id || ev.iou_id || i}-${ev.at}`}
                                ev={ev}
                                onResolve={(row) => setResolvingIou(row)}
                            />
                        ))}
                    </div>
                </>
            )}
            {resolvingIou && (
                <ResolveIouModal
                    ev={resolvingIou}
                    onClose={() => setResolvingIou(null)}
                    onResolved={async () => {
                        setResolvingIou(null);
                        await load(date);
                    }}
                />
            )}
        </div>
    );
}

export function DeductionRow({ ev, onResolve }) {
    const isIou = ev.kind === "iou";
    const time = ev.at ? new Date(ev.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "";
    return (
        <div
            data-testid={`deduction-row-${ev.booking_id || ev.iou_id}`}
            data-kind={ev.kind}
            className={`px-5 py-3 flex items-center justify-between gap-3 ${
                isIou ? "bg-rose-950/30" : ""
            }`}
        >
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap text-sm">
                    {isIou ? (
                        <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 bg-rose-700 text-white rounded-sm font-bold">
                            <AlertCircle className="w-3 h-3" /> IOU
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 bg-emerald-700/70 text-emerald-50 rounded-sm">
                            <CheckCircle2 className="w-3 h-3" /> -1
                        </span>
                    )}
                    {ev.athlete_id ? (
                        <AthleteNameLink
                            athleteId={ev.athlete_id}
                            name={ev.athlete_name || "(unknown athlete)"}
                            testid={`deduction-athlete-${ev.booking_id || ev.iou_id}`}
                            className="font-medium text-white hover:text-brand-red underline-offset-2 hover:underline transition-colors truncate"
                        />
                    ) : ev.parent_user_id ? (
                        <Link
                            to={`/coach?lead=${ev.parent_user_id}`}
                            className="font-medium hover:text-brand-red truncate"
                        >
                            {ev.athlete_name || "(unknown athlete)"}
                        </Link>
                    ) : (
                        <span className="font-medium truncate">{ev.athlete_name || "(unknown athlete)"}</span>
                    )}
                    {!isIou && ev.pack_label && (
                        <span className="text-[11px] text-zinc-400 truncate">· {ev.pack_label}</span>
                    )}
                </div>
                <div className="mt-0.5 text-[11px] text-zinc-400 flex items-center gap-2 flex-wrap">
                    <span>{time}</span>
                    {ev.by_name && <span>· by {ev.by_name}</span>}
                    {!isIou && typeof ev.remaining === "number" && (
                        <span className={ev.remaining === 0 ? "text-amber-400 font-semibold" : "text-zinc-300"}>
                            · pack now: <strong className="text-white">{ev.remaining} left</strong>
                        </span>
                    )}
                    {isIou && (
                        <span className="text-rose-300 font-semibold">· Collect payment — no pack on file</span>
                    )}
                </div>
            </div>
            {isIou && ev.iou_id && (
                <button
                    type="button"
                    data-testid={`iou-resolve-${ev.iou_id}`}
                    onClick={() => onResolve?.(ev)}
                    className="shrink-0 text-[10px] tracking-[0.2em] uppercase border border-rose-500 bg-rose-600 hover:bg-rose-500 text-white px-3 py-2 transition-colors"
                >
                    Resolve
                </button>
            )}
        </div>
    );
}

export function ResolveIouModal({ ev, onClose, onResolved }) {
    const [resolution, setResolution] = useState("paid_offline");
    const [method, setMethod] = useState("cash");
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        setSubmitting(true);
        try {
            const r = await axios.post(
                `${API}/coach/ious/${ev.iou_id}/resolve`,
                {
                    resolution,
                    note: note.trim() || null,
                    method: resolution === "paid_offline" ? method : null,
                },
                { withCredentials: true },
            );
            const label = resolution === "paid_offline" ? `paid via ${method}`
                : resolution === "comped" ? "comped"
                : "applied to pack";
            if (r.data?.pack_used) {
                const p = r.data.pack_used;
                toast.success(`✓ IOU resolved — ${label}`, {
                    description: `1 session deducted from ${p.pack_label || "pack"} · ${p.remaining} left`,
                    duration: 6000,
                    className: "border-emerald-600 bg-emerald-950/90 text-emerald-100",
                });
            } else {
                toast.success(`✓ IOU resolved — ${label}`, { duration: 5000 });
            }
            onResolved?.();
        } catch (e) {
            toast.error("Couldn't resolve", { description: e?.response?.data?.detail });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="resolve-iou-modal">
            <div className="bg-zinc-950 border border-zinc-800 max-w-md w-full">
                <div className="px-5 py-4 border-b border-zinc-900 flex items-center justify-between">
                    <div className="text-sm font-semibold tracking-wide">Resolve IOU</div>
                    <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="px-5 py-4 space-y-4">
                    <div className="text-xs text-zinc-400">
                        <div>Athlete: <span className="text-white">{ev.athlete_name || "—"}</span></div>
                        <div>Date: <span className="text-white">{ev.at ? new Date(ev.at).toLocaleDateString() : "—"}</span></div>
                    </div>
                    <div>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">Resolution</div>
                        <div className="space-y-2">
                            {[
                                { v: "paid_offline", label: "Paid offline", desc: "Cash, Venmo, Zelle, check, etc." },
                                { v: "via_pack", label: "Apply to pack", desc: "Burn 1 session from the newest active pack." },
                                { v: "comped", label: "Comped", desc: "Gym is eating the cost. Note encouraged." },
                            ].map((opt) => (
                                <label
                                    key={opt.v}
                                    className={`flex items-start gap-2 p-2 border cursor-pointer transition-colors ${
                                        resolution === opt.v ? "border-amber-500 bg-amber-950/30" : "border-zinc-800 hover:border-zinc-700"
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="iou-resolution"
                                        data-testid={`iou-resolve-${opt.v}`}
                                        value={opt.v}
                                        checked={resolution === opt.v}
                                        onChange={() => setResolution(opt.v)}
                                        className="mt-0.5"
                                    />
                                    <div>
                                        <div className="text-sm text-white">{opt.label}</div>
                                        <div className="text-[11px] text-zinc-400">{opt.desc}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                    {resolution === "paid_offline" && (
                        <label className="block">
                            <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">Method</span>
                            <select
                                data-testid="iou-method"
                                value={method}
                                onChange={(e) => setMethod(e.target.value)}
                                className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-white focus:border-brand-red focus:outline-none"
                            >
                                <option value="cash">Cash</option>
                                <option value="venmo">Venmo</option>
                                <option value="zelle">Zelle</option>
                                <option value="check">Check</option>
                                <option value="other">Other</option>
                            </select>
                        </label>
                    )}
                    <label className="block">
                        <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">Note (optional)</span>
                        <input
                            type="text"
                            data-testid="iou-note"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder={resolution === "comped" ? "Why comped? (audit trail)" : "Optional note"}
                            className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-white focus:border-brand-red focus:outline-none"
                        />
                    </label>
                </div>
                <div className="px-5 py-3 border-t border-zinc-900 flex items-center justify-between gap-2">
                    <button type="button" onClick={onClose} className="text-[11px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white px-3 py-2">
                        Cancel
                    </button>
                    <button
                        type="button"
                        data-testid="iou-resolve-submit"
                        onClick={submit}
                        disabled={submitting}
                        className="text-[11px] tracking-[0.2em] uppercase bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-4 py-2 disabled:opacity-50 inline-flex items-center gap-2"
                    >
                        {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
                        Resolve
                    </button>
                </div>
            </div>
        </div>
    );
}

